import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UsersThree, Plus, Trash, PencilSimple, Crown, ShieldCheck, User, EnvelopeSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Field, NativeSelect, Confirm, Tag } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";

const ROLE = { owner: ["İşveren", Crown, "approved"], admin: ["Yönetici", ShieldCheck, "collection"], user: ["Kullanıcı", User, "neutral"] };
const LEVEL = [["none", "Yok"], ["view", "Görüntüle"], ["edit", "Düzenle"]];

function UserDialog({ open, onOpenChange, target, modules, onSaved }) {
  const { user: me } = useAuth();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(target ? { name: target.name, role: target.role, password: "", perms: { ...target.perms } }
      : { email: "", name: "", role: "user", perms: Object.fromEntries(modules.map((m) => [m.key, "view"])) });
  }, [open, target, modules]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (target) {
        const body = { name: f.name, role: f.role, perms: f.perms };
        if (f.password) body.password = f.password;
        await api.put(`/users/${target.id}`, body);
        toast.success("Kullanıcı güncellendi");
      } else {
        const { data } = await api.post("/users", f);
        if (data.invitation_status === "sent") toast.success("Kullanıcı eklendi ve davet e-postası gönderildi");
        else toast.warning("Kullanıcı eklendi fakat davet gönderilemedi. Listedeki yeniden gönder seçeneğini kullanın.");
      }
      onOpenChange(false);
      onSaved();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-[4px]" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{target ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı"}</DialogTitle>
          <DialogDescription>Yöneticiler tüm modüllere erişir; kullanıcılar için modül bazında yetki seçin.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {!target && <Field label="E-posta"><Input required type="email" value={f.email || ""} onChange={set("email")} data-testid="user-email-input" /></Field>}
            <Field label="Ad Soyad"><Input required value={f.name || ""} onChange={set("name")} data-testid="user-name-input" /></Field>
            {target && !target.invitation_pending && <Field label="Yeni Şifre (boş = değişmez)"><Input type="password" minLength={f.password ? 8 : 0} value={f.password || ""} onChange={set("password")} data-testid="user-password-input" /></Field>}
            <Field label="Rol">
              <NativeSelect value={f.role || "user"} onChange={set("role")} data-testid="user-role-select">
                <option value="user">Kullanıcı</option>
                {me?.role === "owner" && <option value="admin">Yönetici</option>}
              </NativeSelect>
            </Field>
          </div>
          {(!target || target.invitation_pending) && <p className="border-l-2 border-teal-600 bg-teal-50 p-3 text-xs text-teal-900" data-testid="user-invitation-info">Kullanıcıya 24 saat geçerli, tek kullanımlık şifre belirleme bağlantısı gönderilir. Şifre e-postayla paylaşılmaz.</p>}
          {f.role === "user" && (
            <div className="border border-border" data-testid="user-perms-matrix">
              <div className="grid grid-cols-[minmax(0,1fr)_repeat(3,60px)] sm:grid-cols-[1fr_repeat(3,90px)] bg-muted/60 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span>Modül</span>{LEVEL.map(([, l]) => <span key={l} className="text-center">{l}</span>)}
              </div>
              {modules.map((m) => (
                <div key={m.key} className="grid grid-cols-[minmax(0,1fr)_repeat(3,60px)] sm:grid-cols-[1fr_repeat(3,90px)] items-center border-t border-border px-3 py-2 text-sm" data-testid={`perm-row-${m.key}`}>
                  <span>{m.label}</span>
                  {LEVEL.map(([v]) => (
                    <label key={v} className="flex justify-center">
                      <input type="radio" name={`perm-${m.key}`} checked={(f.perms?.[m.key] || "none") === v} onChange={() => setF({ ...f, perms: { ...f.perms, [m.key]: v } })} className="h-4 w-4 accent-teal-700" data-testid={`perm-${m.key}-${v}`} />
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-[4px]" onClick={() => onOpenChange(false)} data-testid="user-cancel-button">Vazgeç</Button>
            <Button type="submit" className="rounded-[4px]" disabled={busy} data-testid="user-save-button">{busy ? "Kaydediliyor…" : target ? "Kaydet" : "Ekle ve Davet Gönder"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPanel() {
  const { user: me } = useAuth();
  const [users, reload] = useLoad(() => api.get("/users").then((r) => r.data), []);
  const [modules] = useLoad(() => api.get("/users/modules").then((r) => r.data), []);
  const [dlg, setDlg] = useState(null);
  const [sending, setSending] = useState(null);
  const resend = async (u) => {
    setSending(u.id);
    try {
      const { data } = await api.post(`/users/${u.id}/invite`);
      if (data.invitation_status === "sent") toast.success("Yeni davet gönderildi. Önceki bağlantı artık geçersiz.");
      else toast.error(data.invitation_error);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSending(null); }
  };
  const canManage = (u) => u.role !== "owner" && (u.role !== "admin" || me?.role === "owner");
  const del = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("Kullanıcı silindi"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-6" data-testid="users-panel">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 font-display text-lg font-bold"><UsersThree size={18} /> Kullanıcılar ve Yetkiler</h3>
          <p className="text-xs text-muted-foreground">{me?.role === "owner" ? "İşveren olarak yönetici ve kullanıcı ekleyip silebilir, yetki verebilirsiniz." : "Yönetici olarak kullanıcı ekleyip silebilir, yetkilerini düzenleyebilirsiniz."}</p>
        </div>
        <Button className="rounded-[4px]" onClick={() => setDlg({ target: null })} data-testid="new-user-button"><Plus /> Kullanıcı Ekle</Button>
      </div>
      <div className="overflow-x-auto"><table className="w-full min-w-[700px] text-sm" data-testid="users-table">
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground"><th className="pb-2">Ad</th><th className="pb-2">E-posta</th><th className="pb-2">Rol</th><th className="pb-2">Yetkiler</th><th className="pb-2">Davet</th><th /></tr></thead>
        <tbody>
          {(users || []).map((u) => {
            const [label, Icon, tone] = ROLE[u.role] || ROLE.user;
            const edits = Object.values(u.perms || {}).filter((v) => v === "edit").length;
            const views = Object.values(u.perms || {}).filter((v) => v === "view").length;
            return (
              <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.email}`}>
                <td className="py-2 font-medium">{u.name}{u.id === me?.id && <span className="ml-1 text-xs text-muted-foreground">(siz)</span>}</td>
                <td className="py-2 text-muted-foreground">{u.email}</td>
                <td className="py-2"><Tag tone={tone}><Icon size={12} className="mr-1" /> {label}</Tag></td>
                <td className="py-2 text-xs text-muted-foreground">{u.role === "user" ? `${edits} düzenle · ${views} görüntüle` : "Tüm modüller"}</td>
                <td className="py-2 text-xs" data-testid={`user-invitation-${u.email}`}>
                  {!u.invitation_pending ? <span className="text-teal-700">Aktif</span> : <>
                    <span className={u.invitation_status === "failed" ? "text-red-600" : "text-amber-700"}>{u.invitation_status === "failed" ? "Gönderilemedi" : new Date(u.invitation_expires_at) < new Date() ? "Davet süresi doldu" : u.invitation_status === "sent" ? "Kabul bekleniyor" : "Gönderim bekleniyor"}</span>
                    {u.invitation_error && <p className="mt-1 max-w-[180px] text-red-600" data-testid={`user-invitation-error-${u.id}`}>{u.invitation_error}</p>}
                    {canManage(u) && <Button size="sm" variant="ghost" className="mt-1 flex px-0 text-teal-700" disabled={sending === u.id} onClick={() => resend(u)} data-testid={`user-resend-${u.email}`}><EnvelopeSimple />{sending === u.id ? "Gönderiliyor…" : "Yeniden gönder"}</Button>}
                  </>}
                </td>
                <td className="py-2 text-right">
                  {canManage(u) && (<>
                    <Button size="icon" variant="ghost" onClick={() => setDlg({ target: u })} data-testid={`user-edit-${u.email}`}><PencilSimple /></Button>
                    {u.id !== me?.id && <Confirm title="Kullanıcıyı sil" description={`${u.email} silinsin mi? Bu kullanıcı artık giriş yapamaz.`} testid={`user-delete-confirm-${u.email}`}
                      onConfirm={() => del(u)} trigger={<Button size="icon" variant="ghost" className="text-red-600" data-testid={`user-delete-${u.email}`}><Trash /></Button>} />}
                  </>)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table></div>
      <UserDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} target={dlg?.target} modules={modules || []} onSaved={reload} />
    </div>
  );
}
